import { AlertCircle, CheckCircle, FileText, Lock } from 'lucide-react';
import React, { useRef, useState } from 'react';
import './ResumeUpload.css';

interface ResumeUploadProps {
  onUploadComplete: (data: any) => void;
  onSkip: () => void;
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({ onUploadComplete, onSkip }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress('Uploading your resume...');

    try {
      // Import API service dynamically
      const api = (await import('../../services/api')).default;

      // Upload file
      setUploadProgress('Extracting text from PDF...');
      const result = await api.uploadResume(file);

      setUploadProgress('Parsing resume data...');
      
      // Notify parent with parsed data
      onUploadComplete(result.data);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload resume. Please try again.');
      setIsUploading(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <h1>Upload Your Resume</h1>
        <p className="upload-subtitle">
          Let's skip the basics! Upload your existing resume and we'll focus on 
          making it even better through our conversational AI.
        </p>
      </div>

      <div
        className={`upload-dropzone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!isUploading ? handleBrowseClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileInput}
          style={{ display: 'none' }}
          disabled={isUploading}
        />

        {!isUploading ? (
          <>
            <div className="upload-icon">
              <FileText size={48} />
            </div>
            <h3>Drag & Drop Your Resume Here</h3>
            <p>or click to browse</p>
            <p className="upload-hint">PDF only • Max 5MB</p>
          </>
        ) : (
          <>
            <div className="upload-spinner">
              <div className="spinner"></div>
            </div>
            <h3>{uploadProgress}</h3>
            <p className="upload-progress-text">This may take 10-15 seconds...</p>
          </>
        )}
      </div>

      {error && (
        <div className="upload-error">
          <AlertCircle size={18} className="error-icon" />
          {error}
        </div>
      )}

      <div className="upload-benefits">
        <h3>Why upload your resume?</h3>
        <ul className="benefits-list">
          <li>
            <CheckCircle size={18} className="check-icon" />
            Saves 10-15 minutes of manual data entry
          </li>
          <li>
            <CheckCircle size={18} className="check-icon" />
            Our AI will extract your experience, education, and skills
          </li>
          <li>
            <CheckCircle size={18} className="check-icon" />
            You can still edit and refine everything before finalizing
          </li>
          <li>
            <CheckCircle size={18} className="check-icon" />
            Focus on deep-dive questions about your projects and impact
          </li>
        </ul>
      </div>

      <div className="upload-actions">
        <button className="btn-skip" onClick={onSkip} disabled={isUploading}>
          Skip & Build From Scratch
        </button>
      </div>

      <div className="upload-security">
        <Lock size={16} />
        <p>Your resume is encrypted and secure. We never share your data.</p>
      </div>
    </div>
  );
};

export default ResumeUpload;

